// src/pages/HomeLoanPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Phone, Mail, Check, ChevronRight,
  Home, Building2, Calculator, FileText, Shield,
  Clock, DollarSign, Percent, Users, Award,
  Star, Quote, ArrowRight, Search, Menu,
} from 'lucide-react';

// ─── DATA ───────────────────────────────────────────────────────────────────

const LOAN_FEATURES = [
  {
    icon: Percent,
    title: 'Competitive Rates',
    desc: 'Starting from 6.9% p.a. with flexible repayment options.',
    color: '#c17e0aff',
  },
  {
    icon: Clock,
    title: 'Quick Approval',
    desc: 'Get in-principle approval within 24 hours.',
    color: '#c08806ff',
  },
  {
    icon: DollarSign,
    title: 'High Loan Amount',
    desc: 'Up to 90% of property value with flexible tenure.',
    color: '#ca9606ff',
  },
  {
    icon: Shield,
    title: 'Balance Transfer',
    desc: 'Transfer existing home loan for better rates.',
    color: '#c27407ff',
  },
  {
    icon: Users,
    title: 'Joint Application',
    desc: 'Apply jointly to enhance eligibility.',
    color: '#ba8705ff',
  },
  {
    icon: Award,
    title: 'Free Valuation',
    desc: 'Free property valuation and legal verification.',
    color: '#cf970aff',
  },
];

const ELIGIBILITY_CRITERIA = [
  { label: 'Minimum Income', value: '₹3 Lakhs/annum' },
  { label: 'Age Limit', value: '21 - 65 years' },
  { label: 'Employment', value: 'Salaried/Self-employed' },
  { label: 'Credit Score', value: 'CIBIL 750+' },
];

const DOCUMENTS_REQUIRED = [
  'Identity Proof (Aadhar/PAN)',
  'Address Proof',
  'Income Proof (Salary/ITR)',
  'Property Documents',
  'Bank Statements (6 months)',
];

const TESTIMONIALS = [
  {
    name: 'Amit Kumar',
    location: 'Mumbai',
    rating: 5,
    text: 'Smooth process with excellent interest rates. Got approval in just 2 days.',
  },
  {
    name: 'Sneha Patel',
    location: 'Pune',
    rating: 5,
    text: 'Best home loan experience! The team guided us through every step.',
  },
  {
    name: 'Rajesh Sharma',
    location: 'Delhi',
    rating: 5,
    text: 'Professional service with transparent terms. No hidden charges.',
  },
];

const FAQS = [
  {
    q: 'What is the maximum loan amount?',
    a: 'You can get up to 90% of the property value, subject to eligibility and property valuation.',
  },
  {
    q: 'What is the processing fee?',
    a: 'Processing fee is 0.5% of the loan amount, subject to a minimum of ₹5,000.',
  },
  {
    q: 'Can I prepay the loan?',
    a: 'Yes, you can prepay without any charges after 12 months.',
  },
  {
    q: 'How long does approval take?',
    a: 'In-principle approval within 24 hours. Full approval in 7-15 working days.',
  },
];

// ─── UNSPLASH IMAGES ───────────────────────────────────────────────────────
const UNSPLASH_IMAGES = {
  hero: [
    'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1560185127-6ed189bf02f4?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&h=400&fit=crop',
  ],
};

// ─── HOOK: scroll-triggered animation ───────────────────────────────────────
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

// ─── REUSABLE SECTION COMPONENT ────────────────────────────────────────────────
function Section({ children, className = '', style = {} }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: 'opacity .7s ease, transform .7s ease',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── SECTION HEADING ─────────────────────────────────────────────────────────
function SectionHeading({ eyebrow, title, subtitle, light = false }) {
  return (
    <div className="text-center mb-12">
      <div className="inline-flex items-center gap-3 mb-4">
        <div className="h-px w-8" style={{ background: '#c7a705ff' }} />
        <span className="text-[10px] font-bold tracking-[.28em] uppercase" style={{ color: '#c4b404ff' }}>
          {eyebrow}
        </span>
        <div className="h-px w-8" style={{ background: '#c1be08ff' }} />
      </div>
      <h2
        className="font-bold leading-tight mb-3"
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 'clamp(28px,4vw,44px)',
          color: light ? '#fff' : '#0b1220',
        }}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className="max-w-xl mx-auto leading-relaxed"
          style={{ fontSize: '14px', color: light ? 'rgba(255,255,255,.6)' : 'rgba(11,18,32,.55)' }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ─── FEATURE CARD ─────────────────────────────────────────────────────────────
function FeatureCard({ feature, index }) {
  const Icon = feature.icon;
  const [ref, visible] = useReveal();
  
  return (
    <div
      ref={ref}
      className="group relative p-7 bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity .6s ease ${index * 0.1}s, transform .6s ease ${index * 0.1}s`,
        border: '1px solid #e5e7eb',
      }}
    >
      <div
        className="w-12 h-12 flex items-center justify-center mb-5 rounded-lg"
        style={{ background: 'rgba(37,99,235,.08)' }}
      >
        <Icon size={20} color="#bb9f05ff" />
      </div>

      <h3 className="font-bold text-lg mb-2 text-gray-800">
        {feature.title}
      </h3>

      <p className="text-sm text-gray-500 leading-relaxed">
        {feature.desc}
      </p>
    </div>
  );
}

// ─── TESTIMONIAL CARD ─────────────────────────────────────────────────────────
function TestimonialCard({ testimonial, index }) {
  const [ref, visible] = useReveal();
  
  return (
    <div
      ref={ref}
      className="p-6 bg-white rounded-lg shadow-sm"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity .6s ease ${index * 0.15}s, transform .6s ease ${index * 0.15}s`,
        border: '1px solid #e5e7eb',
      }}
    >
      <div className="flex gap-1 mb-3">
        {Array.from({ length: testimonial.rating }).map((_, s) => (
          <Star key={s} size={14} color="#f59e0b" fill="#f59e0b" />
        ))}
      </div>

      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        "{testimonial.text}"
      </p>

      <div className="h-px bg-gray-200 mb-3" />

      <div>
        <div className="font-semibold text-gray-800">{testimonial.name}</div>
        <div className="text-xs text-gray-500">{testimonial.location}</div>
      </div>
    </div>
  );
}

// ─── FAQ ITEM ─────────────────────────────────────────────────────────────────
function FAQItem({ faq, index }) {
  const [isOpen, setIsOpen] = useState(false);
  const [ref, visible] = useReveal();
  
  return (
    <div
      ref={ref}
      className="border-b border-gray-200"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: `opacity .6s ease ${index * 0.08}s, transform .6s ease ${index * 0.08}s`,
      }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full py-4 text-left hover:bg-gray-50 transition-colors px-4"
      >
        <span className="font-medium text-gray-800">
          {faq.q}
        </span>
        <span className="ml-4 text-blue-600" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .3s ease' }}>
          <ChevronRight size={20} />
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 text-gray-600 text-sm leading-relaxed">
          {faq.a}
        </div>
      )}
    </div>
  );
}

// ─── PAGE ────────────────────────────────────────────────────────────────────
export default function HomeLoanPage() {
  return (
    <div style={{ background: '#f8fafc', fontFamily: "'Inter', sans-serif", overflowX: 'hidden' }}>

     



      {/* ── FEATURES ─────────────────────────────────────────────────────── */}
      <div className="py-20 px-6">
        <Section>
          <SectionHeading
            eyebrow="Why Choose Us"
            title="Home Loan Features"
            subtitle="Comprehensive home loan solutions designed to make your dream home affordable."
          />
        </Section>

        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {LOAN_FEATURES.map((feature, i) => (
            <FeatureCard key={i} feature={feature} index={i} />
          ))}
        </div>
      </div>

      {/* ── ELIGIBILITY ──────────────────────────────────────────────────── */}
      <div className="py-20 px-6" style={{ background: '#0b1220' }}>
        <Section>
          <SectionHeading
            eyebrow="Check Your Eligibility"
            title="Eligibility Criteria"
            subtitle="Simple and transparent requirements to get started."
            light
          />
        </Section>

        <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ELIGIBILITY_CRITERIA.map((item, i) => (
            <div
              key={i}
              className="p-6 rounded-lg"
              style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)' }}
            >
              <div className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">
                {item.label}
              </div>
              <div className="text-xl font-bold text-white">
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── DOCUMENTS ────────────────────────────────────────────────────── */}
      <div className="py-20 px-6">
        <Section>
          <SectionHeading
            eyebrow="Documents Required"
            title="Quick Documentation"
            subtitle="Minimal documentation for hassle-free processing."
          />
        </Section>

        <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-3">
          {DOCUMENTS_REQUIRED.map((doc, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm"
            >
              <Check size={16} color="#cb8008ff" />
              <span className="text-sm text-gray-700">{doc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── TESTIMONIALS ─────────────────────────────────────────────────── */}
      <div className="py-20 px-6" style={{ background: '#0b1220' }}>
        <Section>
          <SectionHeading
            eyebrow="Client Stories"
            title="What Our Clients Say"
            light
          />
        </Section>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <TestimonialCard key={i} testimonial={t} index={i} />
          ))}
        </div>
      </div>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <div className="py-20 px-6">
        <Section>
          <SectionHeading
            eyebrow="Got Questions?"
            title="Frequently Asked Questions"
            subtitle="Find answers to the most common questions."
          />
        </Section>

        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-sm border border-gray-200">
          {FAQS.map((faq, i) => (
            <FAQItem key={i} faq={faq} index={i} />
          ))}
        </div>
      </div>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <div className="py-16 px-6" style={{ background: '#200b0fff' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            Ready to Make Your Dream Home a Reality?
          </h3>
          <p className="text-gray-400 mb-8">
            Get started with your home loan application today.
          </p>
          <a
            href="/inquiry"
            className="inline-flex items-center gap-2 px-10 py-4 font-bold tracking-[.18em] uppercase transition-opacity hover:opacity-85 rounded-lg"
            style={{ background:'#d4b209ff', color:'#fff', fontSize:'11px' }}
          >
            Get Free Consultation<ArrowRight size={16} />
          </a>
        </div>

        
      </div>
    </div>
  );
}